/**
 * Labels voor de gepromoveerde HD-eigenschappen (canonieke keys uit de
 * `propertiesSummary`, zie CHART_MANAGEMENT_PLAN.md §3.3), op de bestaande
 * vertaal-namespace `humanDesign.properties.*`. Pure functies: de caller levert
 * de (taalbewuste) translate-functie.
 */
const P = 'humanDesign.properties';

export type HdTranslateFn = (key: string) => string;

export function hdTypeLabel(translate: HdTranslateFn, hdType: string | null | undefined): string | null {
  if (!hdType) return null;
  if (hdType === 'manifestingGenerator') {
    // Samengesteld zoals HdBasicPropertiesComponent: "Manifesterende" + "Generator".
    return `${translate(`${P}.typeValues.manifesting`)} ${translate(`${P}.typeValues.generator`)}`;
  }
  return translate(`${P}.typeValues.${hdType}`);
}

export function hdAuthorityLabel(translate: HdTranslateFn, hdAuthority: string | null | undefined): string | null {
  return hdAuthority ? translate(`${P}.authorityValues.${hdAuthority}`) : null;
}

export function hdDefinitionLabel(translate: HdTranslateFn, hdDefinition: string | null | undefined): string | null {
  return hdDefinition ? translate(`${P}.definitionValues.${hdDefinition}`) : null;
}

/** "right-10-15-17-18" → "Rechte-Hoek Kruis 10/15 | 17/18"; onbekend formaat valt terug op de key zelf. */
export function hdCrossLabel(translate: HdTranslateFn, crossKey: string | null | undefined): string | null {
  if (!crossKey) return null;
  const match = /^(right|left|juxtaposition)-(\d+)-(\d+)-(\d+)-(\d+)$/.exec(crossKey);
  if (!match) return crossKey;
  const [, angle, pSun, pEarth, dSun, dEarth] = match;
  return `${translate(`${P}.crossAngleValues.${angle}`)} ${pSun}/${pEarth} | ${dSun}/${dEarth}`;
}

export interface HdSummaryLike {
  hdType?: string | null;
  hdAuthority?: string | null;
  hdDefinition?: string | null;
  hdProfile?: string | null;
  hdIncarnationCrossKey?: string | null;
}

/** Compacte éénregelige samenvatting voor de detail-pane; null wanneer (nog) niets gestampt is. */
export function hdSummaryText(translate: HdTranslateFn, item: HdSummaryLike): string | null {
  const parts = [
    hdTypeLabel(translate, item.hdType),
    hdAuthorityLabel(translate, item.hdAuthority),
    hdDefinitionLabel(translate, item.hdDefinition),
    item.hdProfile || null,
    hdCrossLabel(translate, item.hdIncarnationCrossKey),
  ].filter((part): part is string => !!part);

  return parts.length ? parts.join(' · ') : null;
}
